import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyCheckFileStatusQuery } from '@/api/file/checkFileStatus';
import { TEMP_LOCAL_DOCUMENT_ID_PREFIX } from '@/constants';
import { removeChatDocument, updateChatDocumentStatus, type ChatDocument, useAppDispatch } from '@/store';
import { ERROR_STATUS_VALUES, FileStatus, isProcessingStatus } from '@/types/file';
import { UPLOAD_FILE_INFECTED_ERROR, UPLOAD_FILE_SCAN_FAILED_ERROR } from '../constants';

const POLL_INTERVAL_MS = 5000;

/** Chip copy for failures the backend reports as a bare status, with no `validationError`. */
const STATUS_ERROR_MESSAGES: Partial<Record<FileStatus, string>> = {
  [FileStatus.INFECTED]: UPLOAD_FILE_INFECTED_ERROR,
  [FileStatus.SCAN_FAILED]: UPLOAD_FILE_SCAN_FAILED_ERROR,
};

/** `/document/status` answers 404 for documents the backend no longer holds (deleted). */
const isDocumentNotFoundError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as { status?: unknown }).status === 404;

const isTerminalStatus = (status: FileStatus) =>
  status === FileStatus.READY_FOR_CHAT ||
  status === FileStatus.UPLOAD_FAILED ||
  status === FileStatus.VALIDATION_ERROR ||
  status === FileStatus.CONTENT_EXTRACTION_FAILED;

export function useChatFileProcessingPolling(sessionDocuments: ChatDocument[]) {
  const dispatch = useAppDispatch();
  const [checkFileStatus] = useLazyCheckFileStatusQuery();

  // Documents the BE returned 404 for. Never polled again, otherwise the chat waits forever.
  const [missingDocumentIds, setMissingDocumentIds] = useState<ReadonlySet<string>>(() => new Set());

  const inProgressDocs = useMemo(
    () =>
      sessionDocuments.filter(
        (d) =>
          isProcessingStatus(d.status) &&
          !d.documentId.includes(TEMP_LOCAL_DOCUMENT_ID_PREFIX) &&
          !missingDocumentIds.has(d.documentId),
      ),
    [missingDocumentIds, sessionDocuments],
  );

  // A batched 404 doesn't say which document is missing, so check the ids one by one (once).
  const resolveMissingDocumentIds = useCallback(
    async (documentIds: string[]): Promise<string[]> => {
      if (documentIds.length === 1) return documentIds;

      const probed = await Promise.all(
        documentIds.map(async (documentId) => {
          try {
            await checkFileStatus({ fletchDocumentIds: [documentId] }).unwrap();
            return null;
          } catch (error) {
            return isDocumentNotFoundError(error) ? documentId : null;
          }
        }),
      );

      return probed.filter((documentId): documentId is string => documentId !== null);
    },
    [checkFileStatus],
  );

  const inFlightRef = useRef(false);
  const intervalRef = useRef<number | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  useEffect(() => {
    const tick = async () => {
      if (cancelledRef.current) return;
      if (inFlightRef.current) return;
      if (inProgressDocs.length === 0) return;

      inFlightRef.current = true;
      // One batched request per tick, never concurrent.
      const documentIds = inProgressDocs.map((d) => d.documentId);
      try {
        const res = await checkFileStatus({ fletchDocumentIds: documentIds }).unwrap();
        if (cancelledRef.current) return;

        for (const doc of inProgressDocs) {
          const statusItem = res.documentStatuses?.[doc.documentId];
          if (!statusItem) continue;

          const backendStatus = statusItem.status;

          if (statusItem.validationError) {
            // The file chip shows the error, so there's no toast.
            dispatch(
              updateChatDocumentStatus({
                documentId: doc.documentId,
                status: FileStatus.VALIDATION_ERROR,
                error: statusItem.validationError,
              }),
            );

            continue;
          }

          // The BE can report `UPLOADED` again after processing started; don't move back from "processing".
          if (
            backendStatus === FileStatus.UPLOADED &&
            (doc.status === FileStatus.CONTENT_EXTRACTION_IN_PROGRESS || doc.status === FileStatus.SCANNING)
          ) {
            continue;
          }

          if (backendStatus && backendStatus !== doc.status) {
            const error = STATUS_ERROR_MESSAGES[backendStatus as FileStatus];
            const payload: { documentId: string; status: FileStatus; error?: string } = {
              documentId: doc.documentId,
              status: backendStatus as FileStatus,
              ...(error ? { error } : {}),
            };

            dispatch(updateChatDocumentStatus(payload));
          }
        }
      } catch (error) {
        if (cancelledRef.current) return;

        if (isDocumentNotFoundError(error)) {
          const missingIds = await resolveMissingDocumentIds(documentIds);
          if (cancelledRef.current || missingIds.length === 0) return;

          // The documents are gone server-side, so drop them from the input rather than
          // leaving attachments behind that can never be sent.
          console.warn('Dropping chat documents the backend no longer has:', missingIds);
          setMissingDocumentIds((previous) => new Set([...previous, ...missingIds]));
          for (const documentId of missingIds) {
            dispatch(removeChatDocument(documentId));
          }
          return;
        }

        // Swallow other polling errors; next tick will retry.
        console.error('Failed to poll file status:', error);
      } finally {
        inFlightRef.current = false;
      }
    };

    // Clear existing interval when dependency set changes.
    if (intervalRef.current != null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (inProgressDocs.length > 0) {
      void tick(); // start immediately
      intervalRef.current = window.setInterval(() => void tick(), POLL_INTERVAL_MS);
    }

    return () => {
      if (intervalRef.current != null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [checkFileStatus, dispatch, inProgressDocs, resolveMissingDocumentIds]);

  const allTerminal = useMemo(() => {
    if (sessionDocuments.length === 0) return true;
    const relevant = sessionDocuments.filter((d) => !d.documentId.includes(TEMP_LOCAL_DOCUMENT_ID_PREFIX));
    // `UPLOADED` is not terminal; it can still transition to processing/ready.
    return relevant.every((d) => isTerminalStatus(d.status as FileStatus));
  }, [sessionDocuments]);

  const anyFailed = useMemo(
    () => sessionDocuments.some((d) => ERROR_STATUS_VALUES.includes(d.status as FileStatus)),
    [sessionDocuments],
  );

  return { allTerminal, anyFailed };
}
